/**
 * Extended Component Tokens for EroChat Design System
 *
 * This module provides comprehensive component-specific tokens
 * that build upon the base spacing and radius tokens.
 *
 * Component tokens ensure consistent sizing, spacing, and styling
 * across all UI components in the application.
 *
 * @module design-system/tokens/components
 */

import { defaultSpacing } from './spacing';
import { defaultRadius } from './radius';
import { defaultFontWeights } from './typography';

// =============================================================================
// BUTTON COMPONENT TOKENS
// =============================================================================

/**
 * Button size configuration
 */
export interface ButtonSizeTokens {
  /** Button height */
  height: string;
  /** Horizontal padding */
  paddingX: string;
  /** Vertical padding */
  paddingY: string;
  /** Font size */
  fontSize: string;
  /** Icon size (for icon buttons or icon + text) */
  iconSize: string;
  /** Gap between icon and text */
  gap: string;
}

/**
 * Button variant styling tokens
 */
export interface ButtonVariantTokens {
  /** Background color or gradient */
  background: string;
  /** Background color on hover */
  backgroundHover: string;
  /** Background color when pressed/active */
  backgroundActive: string;
  /** Text color */
  textColor: string;
  /** Border width */
  borderWidth: string;
  /** Border color */
  borderColor: string;
  /** Border color on hover */
  borderColorHover: string;
}

/**
 * Complete button component tokens
 */
export interface ButtonComponentTokensExtended {
  /** Size variants */
  sizes: {
    xs: ButtonSizeTokens;
    sm: ButtonSizeTokens;
    md: ButtonSizeTokens;
    lg: ButtonSizeTokens;
    xl: ButtonSizeTokens;
  };
  /** Style variants */
  variants: {
    primary: ButtonVariantTokens;
    secondary: ButtonVariantTokens;
    outline: ButtonVariantTokens;
    ghost: ButtonVariantTokens;
    destructive: ButtonVariantTokens;
    link: ButtonVariantTokens;
  };
  /** Common properties */
  common: {
    fontWeight: number;
    borderRadius: string;
    transitionDuration: string;
    focusRingWidth: string;
    focusRingColor: string;
    focusRingOffset: string;
    disabledOpacity: number;
  };
}

/**
 * Default button component tokens
 */
export const defaultButtonTokens: ButtonComponentTokensExtended = {
  sizes: {
    xs: {
      height: '1.5rem', // 24px
      paddingX: '0.5rem', // 8px
      paddingY: '0.125rem', // 2px
      fontSize: '0.75rem', // 12px
      iconSize: '0.875rem', // 14px
      gap: '0.25rem', // 4px
    },
    sm: {
      height: '2rem', // 32px
      paddingX: '0.75rem', // 12px
      paddingY: '0.25rem', // 4px
      fontSize: '0.875rem', // 14px
      iconSize: '1rem', // 16px
      gap: '0.375rem', // 6px
    },
    md: {
      height: '2.5rem', // 40px
      paddingX: '1rem', // 16px
      paddingY: '0.5rem', // 8px
      fontSize: '0.875rem', // 14px
      iconSize: '1.125rem', // 18px
      gap: '0.5rem', // 8px
    },
    lg: {
      height: '3rem', // 48px
      paddingX: '1.5rem', // 24px
      paddingY: '0.75rem', // 12px
      fontSize: '1rem', // 16px
      iconSize: '1.25rem', // 20px
      gap: '0.5rem', // 8px
    },
    xl: {
      height: '3.5rem', // 56px
      paddingX: '2rem', // 32px
      paddingY: '1rem', // 16px
      fontSize: '1.125rem', // 18px
      iconSize: '1.5rem', // 24px
      gap: '0.75rem', // 12px
    },
  },
  variants: {
    primary: {
      background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
      backgroundHover: 'linear-gradient(135deg, #db2777, #7c3aed)',
      backgroundActive: 'linear-gradient(135deg, #be185d, #6d28d9)',
      textColor: '#ffffff',
      borderWidth: '0',
      borderColor: 'transparent',
      borderColorHover: 'transparent',
    },
    secondary: {
      background: 'rgba(255, 255, 255, 0.1)',
      backgroundHover: 'rgba(255, 255, 255, 0.15)',
      backgroundActive: 'rgba(255, 255, 255, 0.2)',
      textColor: '#ffffff',
      borderWidth: '1px',
      borderColor: 'rgba(255, 255, 255, 0.2)',
      borderColorHover: 'rgba(255, 255, 255, 0.3)',
    },
    outline: {
      background: 'transparent',
      backgroundHover: 'rgba(255, 255, 255, 0.05)',
      backgroundActive: 'rgba(255, 255, 255, 0.1)',
      textColor: '#ffffff',
      borderWidth: '1px',
      borderColor: 'rgba(255, 255, 255, 0.3)',
      borderColorHover: 'rgba(255, 255, 255, 0.5)',
    },
    ghost: {
      background: 'transparent',
      backgroundHover: 'rgba(255, 255, 255, 0.1)',
      backgroundActive: 'rgba(255, 255, 255, 0.15)',
      textColor: '#ffffff',
      borderWidth: '0',
      borderColor: 'transparent',
      borderColorHover: 'transparent',
    },
    destructive: {
      background: '#ef4444',
      backgroundHover: '#dc2626',
      backgroundActive: '#b91c1c',
      textColor: '#ffffff',
      borderWidth: '0',
      borderColor: 'transparent',
      borderColorHover: 'transparent',
    },
    link: {
      background: 'transparent',
      backgroundHover: 'transparent',
      backgroundActive: 'transparent',
      textColor: '#ec4899',
      borderWidth: '0',
      borderColor: 'transparent',
      borderColorHover: 'transparent',
    },
  },
  common: {
    fontWeight: defaultFontWeights.semibold,
    borderRadius: defaultRadius.button,
    transitionDuration: '150ms',
    focusRingWidth: '2px',
    focusRingColor: 'rgba(236, 72, 153, 0.5)',
    focusRingOffset: '2px',
    disabledOpacity: 0.5,
  },
};

// =============================================================================
// INPUT COMPONENT TOKENS
// =============================================================================

/**
 * Input size configuration
 */
export interface InputSizeTokens {
  /** Input height */
  height: string;
  /** Horizontal padding */
  paddingX: string;
  /** Font size */
  fontSize: string;
  /** Icon size */
  iconSize: string;
  /** Label font size */
  labelFontSize: string;
}

/**
 * Input state styling tokens
 */
export interface InputStateTokens {
  /** Border color */
  borderColor: string;
  /** Background color */
  background: string;
  /** Text color */
  textColor: string;
  /** Placeholder color */
  placeholderColor: string;
  /** Box shadow */
  boxShadow: string;
}

/**
 * Complete input component tokens
 */
export interface InputComponentTokensExtended {
  /** Size variants */
  sizes: {
    sm: InputSizeTokens;
    md: InputSizeTokens;
    lg: InputSizeTokens;
  };
  /** State styles */
  states: {
    default: InputStateTokens;
    hover: InputStateTokens;
    focus: InputStateTokens;
    disabled: InputStateTokens;
    error: InputStateTokens;
    success: InputStateTokens;
  };
  /** Common properties */
  common: {
    borderWidth: string;
    borderRadius: string;
    transitionDuration: string;
    disabledOpacity: number;
  };
}

/**
 * Default input component tokens
 */
export const defaultInputTokens: InputComponentTokensExtended = {
  sizes: {
    sm: {
      height: '2rem', // 32px
      paddingX: '0.75rem', // 12px
      fontSize: '0.875rem', // 14px
      iconSize: '1rem', // 16px
      labelFontSize: '0.75rem', // 12px
    },
    md: {
      height: '2.5rem', // 40px
      paddingX: '1rem', // 16px
      fontSize: '0.875rem', // 14px
      iconSize: '1.125rem', // 18px
      labelFontSize: '0.875rem', // 14px
    },
    lg: {
      height: '3rem', // 48px
      paddingX: '1.25rem', // 20px
      fontSize: '1rem', // 16px
      iconSize: '1.25rem', // 20px
      labelFontSize: '1rem', // 16px
    },
  },
  states: {
    default: {
      borderColor: 'rgba(255, 255, 255, 0.2)',
      background: 'rgba(0, 0, 0, 0.3)',
      textColor: '#ffffff',
      placeholderColor: 'rgba(255, 255, 255, 0.4)',
      boxShadow: 'none',
    },
    hover: {
      borderColor: 'rgba(255, 255, 255, 0.3)',
      background: 'rgba(0, 0, 0, 0.35)',
      textColor: '#ffffff',
      placeholderColor: 'rgba(255, 255, 255, 0.5)',
      boxShadow: 'none',
    },
    focus: {
      borderColor: '#ec4899',
      background: 'rgba(0, 0, 0, 0.4)',
      textColor: '#ffffff',
      placeholderColor: 'rgba(255, 255, 255, 0.5)',
      boxShadow: '0 0 0 3px rgba(236, 72, 153, 0.25)',
    },
    disabled: {
      borderColor: 'rgba(255, 255, 255, 0.1)',
      background: 'rgba(0, 0, 0, 0.2)',
      textColor: 'rgba(255, 255, 255, 0.4)',
      placeholderColor: 'rgba(255, 255, 255, 0.2)',
      boxShadow: 'none',
    },
    error: {
      borderColor: '#ef4444',
      background: 'rgba(239, 68, 68, 0.1)',
      textColor: '#ffffff',
      placeholderColor: 'rgba(255, 255, 255, 0.4)',
      boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.2)',
    },
    success: {
      borderColor: '#22c55e',
      background: 'rgba(34, 197, 94, 0.1)',
      textColor: '#ffffff',
      placeholderColor: 'rgba(255, 255, 255, 0.4)',
      boxShadow: '0 0 0 3px rgba(34, 197, 94, 0.2)',
    },
  },
  common: {
    borderWidth: '1px',
    borderRadius: defaultRadius.input,
    transitionDuration: '150ms',
    disabledOpacity: 0.5,
  },
};

// =============================================================================
// CARD COMPONENT TOKENS
// =============================================================================

/**
 * Card variant styling tokens
 */
export interface CardVariantTokens {
  /** Background color */
  background: string;
  /** Border width */
  borderWidth: string;
  /** Border color */
  borderColor: string;
  /** Box shadow */
  boxShadow: string;
  /** Hover background */
  backgroundHover?: string;
  /** Hover shadow */
  boxShadowHover?: string;
}

/**
 * Complete card component tokens
 */
export interface CardComponentTokensExtended {
  /** Style variants */
  variants: {
    default: CardVariantTokens;
    elevated: CardVariantTokens;
    outlined: CardVariantTokens;
    filled: CardVariantTokens;
    glass: CardVariantTokens;
    interactive: CardVariantTokens;
  };
  /** Common properties */
  common: {
    padding: string;
    paddingSm: string;
    paddingLg: string;
    gap: string;
    borderRadius: string;
    transitionDuration: string;
  };
}

/**
 * Default card component tokens
 */
export const defaultCardTokens: CardComponentTokensExtended = {
  variants: {
    default: {
      background: 'rgba(255, 255, 255, 0.05)',
      borderWidth: '1px',
      borderColor: 'rgba(255, 255, 255, 0.1)',
      boxShadow: 'none',
    },
    elevated: {
      background: 'rgba(255, 255, 255, 0.08)',
      borderWidth: '0',
      borderColor: 'transparent',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.4), 0 2px 4px -2px rgb(0 0 0 / 0.3)',
    },
    outlined: {
      background: 'transparent',
      borderWidth: '1px',
      borderColor: 'rgba(255, 255, 255, 0.2)',
      boxShadow: 'none',
    },
    filled: {
      background: 'rgba(255, 255, 255, 0.1)',
      borderWidth: '0',
      borderColor: 'transparent',
      boxShadow: 'none',
    },
    glass: {
      background: 'rgba(255, 255, 255, 0.05)',
      borderWidth: '1px',
      borderColor: 'rgba(255, 255, 255, 0.1)',
      boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
    },
    interactive: {
      background: 'rgba(255, 255, 255, 0.05)',
      borderWidth: '1px',
      borderColor: 'rgba(255, 255, 255, 0.1)',
      boxShadow: 'none',
      backgroundHover: 'rgba(255, 255, 255, 0.08)',
      boxShadowHover: '0 4px 6px -1px rgb(0 0 0 / 0.3)',
    },
  },
  common: {
    padding: defaultSpacing.lg,
    paddingSm: defaultSpacing.md,
    paddingLg: defaultSpacing.xl,
    gap: defaultSpacing.md,
    borderRadius: defaultRadius.card,
    transitionDuration: '200ms',
  },
};

// =============================================================================
// NAVIGATION COMPONENT TOKENS
// =============================================================================

/**
 * Complete navigation component tokens
 */
export interface NavigationComponentTokensExtended {
  /** Height values */
  heights: {
    desktop: string;
    mobile: string;
    mobileBottom: string;
    breadcrumbs: string;
  };
  /** Spacing values */
  spacing: {
    itemPaddingX: string;
    itemPaddingY: string;
    itemGap: string;
    sectionGap: string;
  };
  /** Styling values */
  styling: {
    background: string;
    backgroundBlur: string;
    borderColor: string;
    activeIndicatorColor: string;
    activeIndicatorHeight: string;
    itemHoverBackground: string;
    itemActiveBackground: string;
  };
}

/**
 * Default navigation component tokens
 */
export const defaultNavigationTokens: NavigationComponentTokensExtended = {
  heights: {
    desktop: '4rem', // 64px
    mobile: '3.5rem', // 56px
    mobileBottom: '4rem', // 64px
    breadcrumbs: '3rem', // 48px
  },
  spacing: {
    itemPaddingX: defaultSpacing.md,
    itemPaddingY: defaultSpacing.sm,
    itemGap: defaultSpacing.xs,
    sectionGap: defaultSpacing.lg,
  },
  styling: {
    background: 'rgba(0, 0, 0, 0.8)',
    backgroundBlur: 'blur(12px)',
    borderColor: 'rgba(255, 255, 255, 0.1)',
    activeIndicatorColor: '#ec4899',
    activeIndicatorHeight: '2px',
    itemHoverBackground: 'rgba(255, 255, 255, 0.1)',
    itemActiveBackground: 'rgba(255, 255, 255, 0.15)',
  },
};

// =============================================================================
// MODAL COMPONENT TOKENS
// =============================================================================

/**
 * Modal size configuration
 */
export interface ModalSizeTokens {
  /** Maximum width */
  maxWidth: string;
  /** Padding */
  padding: string;
}

/**
 * Complete modal component tokens
 */
export interface ModalComponentTokensExtended {
  /** Size variants */
  sizes: {
    xs: ModalSizeTokens;
    sm: ModalSizeTokens;
    md: ModalSizeTokens;
    lg: ModalSizeTokens;
    xl: ModalSizeTokens;
    full: ModalSizeTokens;
  };
  /** Backdrop styling */
  backdrop: {
    background: string;
    blur: string;
  };
  /** Content styling */
  content: {
    background: string;
    borderRadius: string;
    borderColor: string;
    boxShadow: string;
  };
  /** Animation */
  animation: {
    duration: string;
    easing: string;
  };
}

/**
 * Default modal component tokens
 */
export const defaultModalTokens: ModalComponentTokensExtended = {
  sizes: {
    xs: {
      maxWidth: '20rem', // 320px
      padding: defaultSpacing.md,
    },
    sm: {
      maxWidth: '24rem', // 384px
      padding: defaultSpacing.lg,
    },
    md: {
      maxWidth: '28rem', // 448px
      padding: defaultSpacing.lg,
    },
    lg: {
      maxWidth: '32rem', // 512px
      padding: defaultSpacing.xl,
    },
    xl: {
      maxWidth: '48rem', // 768px
      padding: defaultSpacing.xl,
    },
    full: {
      maxWidth: '100%',
      padding: defaultSpacing.xl,
    },
  },
  backdrop: {
    background: 'rgba(0, 0, 0, 0.7)',
    blur: 'blur(4px)',
  },
  content: {
    background: 'rgba(20, 20, 20, 0.95)',
    borderRadius: defaultRadius.modal,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.7), 0 0 0 1px rgb(255 255 255 / 0.1)',
  },
  animation: {
    duration: '300ms',
    easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
  },
};

// =============================================================================
// AVATAR COMPONENT TOKENS
// =============================================================================

/**
 * Avatar size configuration
 */
export interface AvatarSizeTokens {
  /** Avatar size (width & height) */
  size: string;
  /** Font size for initials */
  fontSize: string;
  /** Icon size */
  iconSize: string;
  /** Status indicator size */
  statusSize: string;
  /** Border width for status ring */
  borderWidth: string;
}

/**
 * Complete avatar component tokens
 */
export interface AvatarComponentTokensExtended {
  /** Size variants */
  sizes: {
    xs: AvatarSizeTokens;
    sm: AvatarSizeTokens;
    md: AvatarSizeTokens;
    lg: AvatarSizeTokens;
    xl: AvatarSizeTokens;
    '2xl': AvatarSizeTokens;
  };
  /** Common properties */
  common: {
    borderRadius: string;
    fallbackBackground: string;
    fallbackTextColor: string;
    borderColor: string;
    borderWidth: string;
  };
  /** Status colors */
  status: {
    online: string;
    offline: string;
    busy: string;
    away: string;
  };
}

/**
 * Default avatar component tokens
 */
export const defaultAvatarTokens: AvatarComponentTokensExtended = {
  sizes: {
    xs: {
      size: '1.5rem', // 24px
      fontSize: '0.625rem', // 10px
      iconSize: '0.75rem', // 12px
      statusSize: '0.5rem', // 8px
      borderWidth: '1px',
    },
    sm: {
      size: '2rem', // 32px
      fontSize: '0.75rem', // 12px
      iconSize: '1rem', // 16px
      statusSize: '0.625rem', // 10px
      borderWidth: '2px',
    },
    md: {
      size: '2.5rem', // 40px
      fontSize: '0.875rem', // 14px
      iconSize: '1.25rem', // 20px
      statusSize: '0.75rem', // 12px
      borderWidth: '2px',
    },
    lg: {
      size: '3rem', // 48px
      fontSize: '1rem', // 16px
      iconSize: '1.5rem', // 24px
      statusSize: '0.875rem', // 14px
      borderWidth: '2px',
    },
    xl: {
      size: '4rem', // 64px
      fontSize: '1.5rem', // 24px
      iconSize: '2rem', // 32px
      statusSize: '1rem', // 16px
      borderWidth: '3px',
    },
    '2xl': {
      size: '6rem', // 96px
      fontSize: '2rem', // 32px
      iconSize: '3rem', // 48px
      statusSize: '1.25rem', // 20px
      borderWidth: '3px',
    },
  },
  common: {
    borderRadius: defaultRadius.avatar,
    fallbackBackground: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
    fallbackTextColor: '#ffffff',
    borderColor: 'rgba(255, 255, 255, 0.2)',
    borderWidth: '2px',
  },
  status: {
    online: '#22c55e',
    offline: '#6b7280',
    busy: '#ef4444',
    away: '#f59e0b',
  },
};

// =============================================================================
// BADGE COMPONENT TOKENS
// =============================================================================

/**
 * Badge size configuration
 */
export interface BadgeSizeTokens {
  /** Vertical padding */
  paddingY: string;
  /** Horizontal padding */
  paddingX: string;
  /** Font size */
  fontSize: string;
  /** Line height */
  lineHeight: string;
  /** Border radius */
  borderRadius: string;
}

/**
 * Complete badge component tokens
 */
export interface BadgeComponentTokensExtended {
  /** Size variants */
  sizes: {
    sm: BadgeSizeTokens;
    md: BadgeSizeTokens;
    lg: BadgeSizeTokens;
  };
  /** Variant colors */
  variants: {
    default: { background: string; textColor: string };
    primary: { background: string; textColor: string };
    secondary: { background: string; textColor: string };
    success: { background: string; textColor: string };
    warning: { background: string; textColor: string };
    error: { background: string; textColor: string };
    info: { background: string; textColor: string };
    outline: { background: string; textColor: string; borderColor: string };
  };
  /** Common properties */
  common: {
    fontWeight: number;
    textTransform: string;
    letterSpacing: string;
  };
}

/**
 * Default badge component tokens
 */
export const defaultBadgeTokens: BadgeComponentTokensExtended = {
  sizes: {
    sm: {
      paddingY: '0.0625rem', // 1px
      paddingX: '0.375rem', // 6px
      fontSize: '0.625rem', // 10px
      lineHeight: '1',
      borderRadius: '0.25rem', // 4px
    },
    md: {
      paddingY: '0.125rem', // 2px
      paddingX: '0.5rem', // 8px
      fontSize: '0.75rem', // 12px
      lineHeight: '1.25',
      borderRadius: '0.375rem', // 6px
    },
    lg: {
      paddingY: '0.25rem', // 4px
      paddingX: '0.75rem', // 12px
      fontSize: '0.875rem', // 14px
      lineHeight: '1.25',
      borderRadius: '0.5rem', // 8px
    },
  },
  variants: {
    default: {
      background: 'rgba(255, 255, 255, 0.1)',
      textColor: 'rgba(255, 255, 255, 0.8)',
    },
    primary: {
      background: 'rgba(236, 72, 153, 0.2)',
      textColor: '#ec4899',
    },
    secondary: {
      background: 'rgba(139, 92, 246, 0.2)',
      textColor: '#a78bfa',
    },
    success: {
      background: 'rgba(34, 197, 94, 0.2)',
      textColor: '#22c55e',
    },
    warning: {
      background: 'rgba(245, 158, 11, 0.2)',
      textColor: '#f59e0b',
    },
    error: {
      background: 'rgba(239, 68, 68, 0.2)',
      textColor: '#ef4444',
    },
    info: {
      background: 'rgba(59, 130, 246, 0.2)',
      textColor: '#3b82f6',
    },
    outline: {
      background: 'transparent',
      textColor: 'rgba(255, 255, 255, 0.8)',
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
  },
  common: {
    fontWeight: 500,
    textTransform: 'none',
    letterSpacing: '0',
  },
};

// =============================================================================
// COMBINED EXPORT
// =============================================================================

/**
 * All extended component tokens combined
 */
export interface AllComponentTokensExtended {
  button: ButtonComponentTokensExtended;
  input: InputComponentTokensExtended;
  card: CardComponentTokensExtended;
  navigation: NavigationComponentTokensExtended;
  modal: ModalComponentTokensExtended;
  avatar: AvatarComponentTokensExtended;
  badge: BadgeComponentTokensExtended;
}

/**
 * Default extended component tokens
 */
export const defaultExtendedComponentTokens: AllComponentTokensExtended = {
  button: defaultButtonTokens,
  input: defaultInputTokens,
  card: defaultCardTokens,
  navigation: defaultNavigationTokens,
  modal: defaultModalTokens,
  avatar: defaultAvatarTokens,
  badge: defaultBadgeTokens,
};

// =============================================================================
// CSS VARIABLE GENERATION
// =============================================================================

/**
 * Generate CSS variables for button tokens
 */
export function generateButtonCSSVariables(
  tokens: ButtonComponentTokensExtended = defaultButtonTokens
): Record<string, string> {
  const vars: Record<string, string> = {};

  // Size variants
  Object.entries(tokens.sizes).forEach(([size, values]) => {
    vars[`--button-${size}-height`] = values.height;
    vars[`--button-${size}-padding-x`] = values.paddingX;
    vars[`--button-${size}-padding-y`] = values.paddingY;
    vars[`--button-${size}-font-size`] = values.fontSize;
    vars[`--button-${size}-icon-size`] = values.iconSize;
    vars[`--button-${size}-gap`] = values.gap;
  });

  // Common properties
  vars['--button-font-weight'] = String(tokens.common.fontWeight);
  vars['--button-border-radius'] = tokens.common.borderRadius;
  vars['--button-transition-duration'] = tokens.common.transitionDuration;
  vars['--button-focus-ring-width'] = tokens.common.focusRingWidth;
  vars['--button-focus-ring-color'] = tokens.common.focusRingColor;
  vars['--button-focus-ring-offset'] = tokens.common.focusRingOffset;
  vars['--button-disabled-opacity'] = String(tokens.common.disabledOpacity);

  return vars;
}

/**
 * Generate CSS variables for input tokens
 */
export function generateInputCSSVariables(
  tokens: InputComponentTokensExtended = defaultInputTokens
): Record<string, string> {
  const vars: Record<string, string> = {};

  // Size variants
  Object.entries(tokens.sizes).forEach(([size, values]) => {
    vars[`--input-${size}-height`] = values.height;
    vars[`--input-${size}-padding-x`] = values.paddingX;
    vars[`--input-${size}-font-size`] = values.fontSize;
    vars[`--input-${size}-icon-size`] = values.iconSize;
    vars[`--input-${size}-label-font-size`] = values.labelFontSize;
  });

  // Common properties
  vars['--input-border-width'] = tokens.common.borderWidth;
  vars['--input-border-radius'] = tokens.common.borderRadius;
  vars['--input-transition-duration'] = tokens.common.transitionDuration;
  vars['--input-disabled-opacity'] = String(tokens.common.disabledOpacity);

  return vars;
}

/**
 * Generate all component CSS variables
 */
export function generateAllComponentCSSVariables(
  tokens: AllComponentTokensExtended = defaultExtendedComponentTokens
): Record<string, string> {
  return {
    ...generateButtonCSSVariables(tokens.button),
    ...generateInputCSSVariables(tokens.input),
    // Card common
    '--card-padding': tokens.card.common.padding,
    '--card-padding-sm': tokens.card.common.paddingSm,
    '--card-padding-lg': tokens.card.common.paddingLg,
    '--card-gap': tokens.card.common.gap,
    '--card-border-radius': tokens.card.common.borderRadius,
    // Navigation
    '--nav-height-desktop': tokens.navigation.heights.desktop,
    '--nav-height-mobile': tokens.navigation.heights.mobile,
    '--nav-height-mobile-bottom': tokens.navigation.heights.mobileBottom,
    // Modal
    '--modal-backdrop-bg': tokens.modal.backdrop.background,
    '--modal-backdrop-blur': tokens.modal.backdrop.blur,
    '--modal-border-radius': tokens.modal.content.borderRadius,
    // Avatar
    '--avatar-border-radius': tokens.avatar.common.borderRadius,
    '--avatar-fallback-bg': tokens.avatar.common.fallbackBackground,
    // Badge
    '--badge-font-weight': String(tokens.badge.common.fontWeight),
  };
}
